/**
 * Lightweight pub-sub for the upload queue state (bug 2026-08-05).
 *
 * The queue is a module-level singleton inside ChunkedUploadService
 * (activePackageId + queueWaiters). Prior to this module the UI had no
 * reliable signal for "this specific upload is currently waiting for its
 * slot" — it inferred from `uploadTelemetryService.status === "waiting"`,
 * which drifted every time pauseInFlight overwrote the status to PAUSED.
 * That produced two visible bugs:
 *   - Queued uploads flipped between hourglass and pause icon depending on
 *     what the ACTIVE upload's pause state was ("pause all" hit them too).
 *   - Brand-new uploads showed the active/pause icon during their init
 *     POST window because markWaiting isn't called until AFTER init.
 *
 * This side-channel emits the queue snapshot whenever the slot ownership
 * changes. store.js subscribes at boot and dispatches it into
 * packagesSlice; UI reads `uploadQueueState.queuedUploadIds` to decide
 * whether to render the hourglass.
 *
 * Matches the pub-sub shape of autoPauseEvents.js and
 * uploadCompletionEvents.js — plain module-level Set, no framework.
 */

const listeners = new Set();

/**
 * @param {{ activeUploadId: string | null, queuedUploadIds: string[] }} snapshot
 */
export const notifyUploadQueueChanged = (snapshot) => {
  if (!snapshot) return;
  for (const listener of listeners) {
    try {
      listener(snapshot);
    } catch (err) {
      // Never let a subscriber's throw break the uploader.
      // eslint-disable-next-line no-console
      console.warn("[uploadQueueEvents] listener threw", err?.message);
    }
  }
};

/**
 * @param {(snapshot: { activeUploadId: string | null, queuedUploadIds: string[] }) => void} listener
 * @returns {() => void} unsubscribe
 */
export const subscribeUploadQueue = (listener) => {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const __resetUploadQueueListenersForTests = () => listeners.clear();
